import { FC, useState } from 'react';
import { Upload as UploadIcon, FileText, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { DropZone } from '../Dropzone/Dropzone';

interface UploadProps {
  onUpload: (file: File) => Promise<void>;
  onUploadMultiple?: (files: File[]) => Promise<void>;
  accept?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  currentFile?: File | null;
  isUploading?: boolean;
  showFilePreview?: boolean;
  onRemove?: () => void;
  uploadText?: string;
  supportedFormatsText?: string;
  multiple?: boolean;
}

export const Upload: FC<UploadProps> = ({
  onUpload,
  onUploadMultiple,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip',
  maxSizeMB = 10,
  disabled = false,
  currentFile = null,
  isUploading = false,
  showFilePreview = true,
  onRemove,
  uploadText = 'Drag and drop your file here, or click to browse',
  supportedFormatsText = 'Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, ZIP',
  multiple = false,
}) => {
  const [localFile, setLocalFile] = useState<File | null>(currentFile);
  const [localUploading, setLocalUploading] = useState(false);

  const displayFile = currentFile || localFile;
  const uploading = isUploading || localUploading;

  const handleFileUpload = async (file: File) => {
    if (!multiple) setLocalFile(file);
    setLocalUploading(true);

    try {
      await onUpload(file);
    } catch (error) {
      console.error('Upload failed:', error);
      if (!multiple) setLocalFile(null);
    } finally {
      setLocalUploading(false);
    }
  };

  const handleMultipleFileUpload = async (files: File[]) => {
    setLocalUploading(true);
    try {
      await onUploadMultiple?.(files);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setLocalUploading(false);
    }
  };

  const handleRemove = () => {
    setLocalFile(null);
    if (onRemove) {
      onRemove();
    }
  };

  const handleError = (error: 'Invalid type' | 'File too large') => {
    if (error === 'Invalid type') {
      toast.error('Invalid file type. Please upload a supported file format.');
    } else if (error === 'File too large') {
      toast.error(`File size exceeds ${maxSizeMB}MB limit. Please upload a smaller file.`);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <DropZone
        onUpload={handleFileUpload}
        onUploadMultiple={onUploadMultiple ? handleMultipleFileUpload : undefined}
        onError={handleError}
        accept={accept}
        maxSizeMB={maxSizeMB}
        disabled={disabled || (uploading && !multiple)}
        multiple={multiple}
      >
        <div className="flex flex-col items-center gap-2 py-6">
          {uploading ? (
            <>
              <Loader2 size={32} className="text-(--primary-O900) animate-spin" />
              <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!">
                Uploading and submitting...
              </p>
            </>
          ) : (
            <>
              <UploadIcon size={32} className="text-(--neutral-black-NB400)" />
              <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!">
                {uploadText}
              </p>
              <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                {supportedFormatsText} (Max {maxSizeMB}MB)
              </p>
            </>
          )}
        </div>
      </DropZone>

      {/* File Preview */}
      {!multiple && showFilePreview && displayFile && (
        <div
          className={`flex items-center gap-2 p-3 border-2 rounded-lg ${
            uploading
              ? 'border-(--primary-O900) bg-(--primary-O50)'
              : 'border-(--neutral-black-NB100)'
          }`}
        >
          {uploading && <Loader2 size={20} className="text-(--primary-O900) animate-spin" />}
          <FileText size={20} className="text-(--primary-O900)" />
          <span className="flex-1 [font-size:var(--fs-p4)]! text-(--neutral-black-NB900)!">
            {displayFile.name}
          </span>
          <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
            {(displayFile.size / 1024 / 1024).toFixed(2)} MB
          </span>
          {!uploading && onRemove && (
            <button
              onClick={handleRemove}
              className="p-1 hover:bg-(--neutral-black-NB100) rounded transition-colors"
            >
              <X size={16} className="text-(--neutral-black-NB600)" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
